import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import UserInteraction from '../../../../Model/UserInteraction';
import AnnouncementCommentInteraction from '../../../../Model/AnnouncementCommentInteraction';

import {
  Container,
  HeartIconOutline,
  HeartIconFilled,
  LikeLoadingSpinner,
} from './styles';
import texts from './texts.json';

const LikeButton = ({
  announcementId, commentId, count, isReadOnly,
}) => {
  const [isLiked, setIsLiked] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const interactionRef = useRef(null);

  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const isComponentMountedRef = useComponentMounted();

  const interaction = useMemo(() => {
    // An aggregated count spans several posts, so there is no single doc a like could target.
    // Skipping the interaction doc also saves a read per aggregated card.
    if (isReadOnly || !loggedInUserId || !announcementId) return null;
    return commentId
      ? new AnnouncementCommentInteraction(loggedInUserId, announcementId, commentId, { disableObserverRefCount: true })
      : new UserInteraction(loggedInUserId, announcementId, { disableObserverRefCount: true });
  }, [isReadOnly, loggedInUserId, announcementId, commentId]);

  const syncLikeState = useCallback(async (doc = interactionRef.current) => {
    if (!doc) return;
    await doc.init({ fetch: !doc.isLoading });
    if (isComponentMountedRef.current) {
      setIsLiked(doc.liked);
    }
  }, [isComponentMountedRef]);

  useEffect(() => {
    if (!interaction) {
      interactionRef.current = null;
      return;
    }

    interactionRef.current = interaction;
    syncLikeState(interaction);
  }, [interaction, syncLikeState]);

  // A like can be toggled from another LikeButton instance (for example, list item vs modal view).
  // Re-sync when count changes so this button's local `isLiked` state stays in sync.
  useEffect(() => {
    syncLikeState();
  }, [count, syncLikeState]);

  const handleToggle = useCallback(async () => {
    if (!interactionRef.current || isLikeLoading) return;
    setIsLikeLoading(true);
    const newLiked = !isLiked;
    const fields = commentId
      ? {
        userId: loggedInUserId, announcementId, commentId, liked: newLiked,
      }
      : {
        userId: loggedInUserId, announcementId, liked: newLiked, viewed: true,
      };
    await interactionRef.current.updateFields(fields);
    if (isComponentMountedRef.current) {
      setIsLiked(newLiked);
      setIsLikeLoading(false);
    }
  }, [isLiked, isLikeLoading, loggedInUserId, announcementId, commentId, isComponentMountedRef]);

  const renderIcon = () => {
    if (isLikeLoading) return <LikeLoadingSpinner size={18} thickness={5} />;
    if (isLiked) return <HeartIconFilled />;
    return <HeartIconOutline />;
  };

  return (
    // Read-only means no interaction doc was fetched, so the heart stays an outline whether or not
    // the coach liked one of the copies. The title says so, rather than letting it read as "not
    // liked" on a control that also does nothing when clicked.
    <Container
      onClick={isReadOnly ? undefined : handleToggle}
      $isReadOnly={isReadOnly}
      {...(isReadOnly && { title: texts.aggregatedLikes })}
    >
      {renderIcon()}
      {count}
    </Container>
  );
};

LikeButton.propTypes = {
  announcementId: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  commentId: PropTypes.string,
  isReadOnly: PropTypes.bool,
};

LikeButton.defaultProps = {
  commentId: null,
  isReadOnly: false,
};

export default LikeButton;
